/**
 * Search page — server-side MongoDB $text search across the full
 * NewsPulse event archive. Results are ranked by relevance (textScore).
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search as SearchIcon } from "lucide-react";
import { npApi } from "./api";
import EventCard from "./EventCard";

export default function NewsPulseSearch() {
  const [sp, setSp] = useSearchParams();
  const [q, setQ] = useState(sp.get("q") || "");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const query = sp.get("q") || "";

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    npApi
      .search(query.trim(), { limit: 50 })
      .then((r) => { if (!cancelled) setResults(r || []); })
      .catch((e) => {
        if (!cancelled) {
          setResults([]);
          setError(e?.message || "search_failed");
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [query]);

  const onSubmit = (e) => {
    e.preventDefault();
    setSp({ q });
  };

  return (
    <div className="max-w-[1280px] mx-auto px-4 lg:px-6 py-10" data-testid="newspulse-search">
      <div className="pb-4 mb-8 border-b border-slate-200">
        <div className="text-xs uppercase tracking-widest text-slate-500">Search</div>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">Find a story</h1>
      </div>
      <form
        onSubmit={onSubmit}
        className="mb-8 flex items-center bg-slate-100 rounded-full pl-5 pr-1.5 py-1.5 border border-transparent focus-within:border-blue-500 focus-within:bg-white transition-colors max-w-2xl"
        data-testid="search-form"
      >
        <SearchIcon className="w-5 h-5 text-slate-400 mr-3" />
        <input
          type="search"
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search the full news archive…"
          className="flex-1 bg-transparent border-0 outline-none text-base py-2 placeholder:text-slate-400"
          data-testid="search-input"
        />
        <button
          type="submit"
          className="rounded-full h-10 px-5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors"
          data-testid="search-submit"
        >
          Search
        </button>
      </form>

      {!query ? (
        <div className="text-slate-500 space-y-2">
          <p className="italic">Type a query above.</p>
          <p className="text-xs text-slate-400">
            Tip: use <code className="px-1 py-0.5 bg-slate-100 rounded">"exact phrase"</code> for phrase match,
            or <code className="px-1 py-0.5 bg-slate-100 rounded">covid -flu</code> to exclude terms.
          </p>
        </div>
      ) : loading ? (
        <p className="text-slate-500" data-testid="search-loading">Searching the archive…</p>
      ) : error ? (
        <p className="text-red-600" data-testid="search-error">Search failed. Try again.</p>
      ) : results.length ? (
        <>
          <p className="text-sm text-slate-500 mb-3" data-testid="search-count">
            {results.length} match{results.length === 1 ? "" : "es"} for <b>"{query}"</b>
            <span className="text-slate-400"> · ranked by relevance</span>
          </p>
          <div data-testid="search-results">
            {results.map((e) => <EventCard key={e.event_id} event={e} />)}
          </div>
        </>
      ) : (
        <p className="text-slate-500 italic" data-testid="search-empty">
          No stories match "{query}" in the archive. Try a broader term.
        </p>
      )}
    </div>
  );
}
